import { Flex, Mask, MDIIcon, Panel, Select } from '@solinkcorp/solink-components';
import Inbox from '@solinkcorp/solink-components/svg/inbox.svg?react';
import { Virtual } from '@solinkcorp/solink-ui';
import classNames from 'classnames';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import AnalyticsLogger from 'models/AnalyticsLogger';
import { PureComponent } from 'react';
import { connect } from 'react-redux';
import {
  NOTIFICATION_UNREAD_COUNT_DISPLAY_LIMIT,
  NOTIFICATIONS_FILTER_BY_STATE,
} from 'vd-constants';
import NotificationItem from './NotificationItem';
import './NotificationsPanel.sass';
class NotificationsPanel extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isOperationInProgress: false,
      newNotificationTimestamp: new Date().getTime(),
      selectedNotificationCount: 0,
      selectedNotificationIds: {},
      lastClickedId: -1,
    };
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentDidUpdate() {
    const { notifications } = this.props;
    const { newNotificationTimestamp } = this.state;
    const latestTime = get(notifications, '[0].created', 0);
    if (latestTime > newNotificationTimestamp) {
      clearTimeout(this.newNotificationDelay);
      clearTimeout(this.newNotificationAnimationDelay);
      this.newNotificationDelay = setTimeout(() => {
        this.setState({ newNotificationTimestamp: latestTime });
        clearTimeout(this.newNotificationAnimationDelay);
        this.newNotificationAnimationDelay = setTimeout(
          () => this.setState({ newNotificationTimestamp: latestTime + 1 }),
          500,
        );
      }, 500);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  onArchive = (index) => {
    const { notifications, onArchive } = this.props;
    onArchive(notifications[index]);
  };

  onMarkAsRead = (index) => {
    const { notifications, onMarkAsRead } = this.props;
    onMarkAsRead(notifications[index]);
  };

  onView = (index, isVideoPage) => {
    const { notifications, onView } = this.props;
    const data = notifications[index];

    this.setState({
      lastClickedId: data.id,
    });

    onView(data, isVideoPage);
  };

  getEventInfo = ({ title, subtitle }) => {
    return `${title} - ${subtitle}`;
  };

  handleFilterChange = ({ value }) => {
    const { onFilter, userId } = this.props;
    onFilter({ filterBy: value });
    AnalyticsLogger.notificationFilterView({ type: value, userId });
  };

  setFilterByRef = (element) => {
    this.filterBySelector = element;
    this.filterBySelector.focus();
  };

  handleCheckNotification = ({ value, index }) => {
    const { notifications } = this.props;
    const { selectedNotificationCount, selectedNotificationIds: source } = this.state;
    const selectedNotificationIds = { ...source };
    const id = get(notifications, [index, 'id']);
    const isNotificationChecked = selectedNotificationIds[id];
    let count = selectedNotificationCount;

    if (id) {
      if (value && !isNotificationChecked) {
        selectedNotificationIds[id] = true;
        count++;
      } else if (isNotificationChecked) {
        delete selectedNotificationIds[id];
        count--;
      }

      this.setState({ selectedNotificationCount: count, selectedNotificationIds });
    } else {
      console.error('The notification does not have an id!');
    }
  };

  archiveMultiple = async () => {
    const { archiveAll } = this.props;
    const { selectedNotificationIds } = this.state;
    this.setState({ isOperationInProgress: true });
    await archiveAll(selectedNotificationIds);
    this.resetSelection();
  };

  markMultipleAsRead = async () => {
    const { markAllAsRead } = this.props;
    const { selectedNotificationIds } = this.state;
    this.setState({ isOperationInProgress: true });
    await markAllAsRead(selectedNotificationIds);
    this.resetSelection();
  };

  resetSelection = () =>
    this.setState({
      isOperationInProgress: false,
      selectedNotificationCount: 0,
      selectedNotificationIds: {},
    });

  rowRenderer = (data, index) => {
    if (!this._isMounted) {
      return null;
    }

    const {
      filterBy,
      useDarkTheme,
      isImpersonating,
      locale,
      readCutoffDate,
      notificationErrorIndicies,
    } = this.props;
    const { newNotificationTimestamp, selectedNotificationIds, lastClickedId } = this.state;

    const disableMarkAsRead =
      isImpersonating ||
      filterBy === NOTIFICATIONS_FILTER_BY_STATE.read ||
      filterBy === NOTIFICATIONS_FILTER_BY_STATE.archived;
    const disableArchive = isImpersonating || filterBy === NOTIFICATIONS_FILTER_BY_STATE.archived;
    const { created, id, state } = data || {};

    const isRead = created < readCutoffDate || state === NOTIFICATIONS_FILTER_BY_STATE.read;
    const isNew = !isRead && created >= newNotificationTimestamp;
    const listItemClass = classNames('list-item', {
      'last-clicked': lastClickedId === id,
      'has-error': notificationErrorIndicies[id],
    });

    return (
      <NotificationItem
        className={listItemClass}
        darkTheme={useDarkTheme}
        data={data}
        disableArchive={disableArchive}
        disableMarkAsRead={disableMarkAsRead}
        id={id}
        index={index}
        isChecked={selectedNotificationIds[id]}
        isNew={isNew}
        isRead={isRead}
        key={id}
        locale={locale}
        onArchive={this.onArchive}
        onCheck={this.handleCheckNotification}
        onMarkAsRead={this.onMarkAsRead}
        onView={this.onView}
      />
    );
  };

  getActionsDisabledTooltip = () => {
    const { filterBy, isImpersonating, locale } = this.props;

    if (filterBy === NOTIFICATIONS_FILTER_BY_STATE.archived) {
      return locale.notifications.disabledOnArchived;
    } else if (isImpersonating) {
      return locale.notifications.disabledWhenImpersonationg;
    }

    return '';
  };

  handleClose = () => {
    const { onClose } = this.props;

    onClose();
    this.handleFilterChange({ value: NOTIFICATIONS_FILTER_BY_STATE.all });
    this.setState({
      selectedNotificationCount: 0,
      selectedNotificationIds: [],
    });
  };

  render() {
    const {
      changeTime,
      count,
      filterBy,
      filterByOptions,
      isImpersonating,
      isLoading,
      locale,
      notifications,
      showAll,
      useDarkTheme,
    } = this.props;

    const { isOperationInProgress, selectedNotificationCount, selectedNotificationIds } =
      this.state;
    const selectedCount = isEmpty(selectedNotificationIds)
      ? locale.notifications.all
      : selectedNotificationCount;
    const unreadCount =
      count > NOTIFICATION_UNREAD_COUNT_DISPLAY_LIMIT
        ? `${NOTIFICATION_UNREAD_COUNT_DISPLAY_LIMIT}+`
        : count;
    const showEmptyState = !isLoading && isEmpty(notifications);
    const showNotificationList = !isLoading && !isEmpty(notifications);
    const actionColorMode = useDarkTheme ? 'line-dark' : 'line-light';
    const selectColorMode = useDarkTheme ? 'dark' : 'light';
    const actionsDisabled = filterBy === NOTIFICATIONS_FILTER_BY_STATE.archived || isImpersonating;
    const actionsDisabledTooltip = this.getActionsDisabledTooltip();
    const newMessageBadge = `${unreadCount} ${locale.notifications.newPlural}`;
    const notificationsPanelClass = classNames('notifications-panel', {
      dark: useDarkTheme,
    });

    return (
      <div className={notificationsPanelClass}>
        <Panel
          actions={[
            {
              badge: selectedCount,
              colorMode: actionColorMode,
              disabled: !count || actionsDisabled,
              onClick: this.archiveMultiple,
              text: locale.notifications.archive,
              tooltip: actionsDisabledTooltip,
            },
            {
              badge: selectedCount,
              colorMode: actionColorMode,
              disabled: !count || actionsDisabled,
              onClick: this.markMultipleAsRead,
              text: locale.notifications.markAsRead,
              tooltip: actionsDisabledTooltip,
            },
          ]}
          bodyClassName="panel-body"
          canConfirm
          changeTime={changeTime}
          closeOnOutsideClick
          filterBy={filterBy}
          fixedFooter
          fixedHeader
          header={
            <div className="panel-header">
              <Flex alignItems="center" justifyContent="flex-start">
                <h1 className="title">{locale.notifications.notifications}</h1>
                {!isLoading && <div className="badge">{newMessageBadge}</div>}
              </Flex>
              <Select
                className="filter-select"
                colorMode={selectColorMode}
                menuHeight={360}
                name="notifications-panel-filter"
                onChange={this.handleFilterChange}
                options={filterByOptions}
                refFunc={this.setFilterByRef}
                value={filterBy}
              />
            </div>
          }
          isFullHeight
          isOpen={showAll}
          onClose={this.handleClose}
          position="right"
          showCloseButton
          type="wide"
        >
          {(isOperationInProgress || isLoading) && (
            <Mask className="loading-state" position="fixed" />
          )}
          {showEmptyState && (
            <Flex
              alignItems="center"
              className="empty-state"
              flexDirection="column"
              justifyContent="center"
            >
              <MDIIcon className="icon-inbox" icon={Inbox} size={58} />
              <div>{locale.notifications.noNotifications}</div>
            </Flex>
          )}
          {showNotificationList && (
            <section className="notifications-list-view">
              <Virtual data={notifications}>{this.rowRenderer}</Virtual>
            </section>
          )}
        </Panel>
      </div>
    );
  }
}

NotificationsPanel.defaultProps = {
  filterByOptions: [],
  onFilter: () => {},
};

const ComposedNotificationsPanel = (props) => {
  return <NotificationsPanel {...props} />;
};

export default connect()(ComposedNotificationsPanel);
